import { Alert, Box, Button, CircularProgress, Divider, Paper, Tab, Tabs, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import MemberPreviewDatePicker from './MemberPreviewDatePicker';
import MemberPreviewTimeList from './MemberPreviewTimeList';
import { isBusyUnavailableError, useGetMemberPreviewQuery, viewerTimeZone } from './memberPreviewApi';
import {
  firstEnabledDate,
  formatSelectedDate,
  previewMonths,
  seriesSummary,
} from './memberPreviewCalendar';
import type { MemberPreviewKind } from './memberPreviewTypes';
import SeriesConflictNote from './SeriesConflictNote';

const MemberViewPanel: React.FC = () => {
  const [kind, setKind] = useState<MemberPreviewKind>('coaching');
  const timeZone = useMemo(viewerTimeZone, []);
  const { currentData: data, error, isLoading, isFetching, refetch } = useGetMemberPreviewQuery({
    kind,
    viewerTimezone: timeZone,
  });
  const [month, setMonth] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedStart, setSelectedStart] = useState<string | null>(null);

  const days = useMemo(() => data?.days ?? [], [data]);
  const months = useMemo(() => previewMonths(days), [days]);

  useEffect(() => {
    const initial = firstEnabledDate(days);
    setSelectedDate(initial);
    setSelectedStart(null);
    setMonth(initial?.slice(0, 7) ?? months[0] ?? null);
  }, [days, months]);

  const slots = days.find((day) => day.date === selectedDate)?.slots ?? [];
  const selectedSlot = slots.find((slot) => slot.start_at === selectedStart);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 3 }}
      id="calendar-settings-member-view-section"
      role="tabpanel"
      aria-labelledby="calendar-settings-section-member-view"
    >
      <Typography sx={{ fontWeight: 600 }}>Your Members' View</Typography>
      <Typography variant="caption" color="text.secondary">
        See the availability they can see for coaching or chem calls
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Tabs
        value={kind}
        onChange={(_, value: MemberPreviewKind) => setKind(value)}
        aria-label="Booking type"
      >
        <Tab value="coaching" label="Coaching" />
        <Tab value="chemistry" label="Chem Call" />
      </Tabs>
      <Box sx={{ mt: 3 }}>
        {isLoading || (isFetching && !data) ? (
          <CircularProgress size={24} aria-label="Loading member view" />
        ) : error ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Try again
              </Button>
            }
          >
            {isBusyUnavailableError(error)
              ? "We couldn't read your calendar, so we can't show what members see right now."
              : "Something went wrong loading your members' view."}
          </Alert>
        ) : !data?.calendar_ready ? (
          <Alert severity="info">Set your availability to see what members see.</Alert>
        ) : !days.some((day) => day.enabled) ? (
          <Alert severity="info">Members currently see no open times.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            {month ? (
              <MemberPreviewDatePicker
                days={days}
                months={months}
                month={month}
                onMonthChange={setMonth}
                selectedDate={selectedDate}
                onSelectDate={(date) => {
                  setSelectedDate(date);
                  setSelectedStart(null);
                }}
              />
            ) : null}
            <Box
              sx={{ flex: 1, borderLeft: { md: 1 }, borderColor: { md: 'divider' }, pl: { md: 3 } }}
            >
              {selectedDate ? (
                <>
                  <Typography sx={{ fontWeight: 600 }}>{formatSelectedDate(selectedDate)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {seriesSummary(data.duration_minutes, data.appointment_count, data.cadence_weeks)}
                  </Typography>
                  <MemberPreviewTimeList
                    slots={slots}
                    timeZone={timeZone}
                    selectedStart={selectedStart}
                    onSelect={setSelectedStart}
                  />
                  {selectedSlot?.coach_conflict ? (
                    <Box sx={{ mt: 2 }}>
                      <SeriesConflictNote
                        conflictingOccurrenceStarts={
                          selectedSlot.coach_conflict.conflicting_occurrence_starts
                        }
                        timeZone={timeZone}
                      />
                    </Box>
                  ) : null}
                </>
              ) : null}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default MemberViewPanel;
